import typia from 'typia';
import { dispatchAction } from '@ardoq/rxbeach';
import { startTour, type TourState } from '@ardoq/product-tours-host';
import { type CataloguedTourId, type TourCatalogueEntry, } from '@ardoq/product-tours';
import { AiControlledCommandId } from 'aiControlledCommands/aiControlledCommandId';
import { defineAiControlledCommand } from 'aiControlledCommands/defineAiControlledCommand';
import { PRODUCT_TOUR_DOMAIN } from 'aiControlledCommands/productTours/domain';
import type { AiControlledCommand } from 'aiControlledCommands/types';
import { WHEN_TO_USE, START_PRODUCT_TOUR_CONTEXT_DESCRIPTION, } from './descriptions';
const INVALID_PAYLOAD_MESSAGE = 'The AI returned an invalid tour to start.';
// typia compiles the JSDoc here into the command's JSON schema, so this comment
// is LLM-facing — keep it accurate.
export type StartProductTourPayload = {
    /** The id of the tour to start. Must be one of the ids in the context. */
    tourId: CataloguedTourId;
};
export const executeStartProductTour = ({ tourId, }: StartProductTourPayload): void => {
    dispatchAction(startTour(tourId));
};
type StartProductTourContext = {
    availableTours: TourCatalogueEntry[];
    tourState: TourState;
};
export const startProductTourCommandDefinition = defineAiControlledCommand<StartProductTourPayload, StartProductTourContext>({
    name: AiControlledCommandId.START_PRODUCT_TOUR,
    requiresAppShell: true,
    domain: PRODUCT_TOUR_DOMAIN,
    description: WHEN_TO_USE,
    parameters: {
        version: "3.1",
        components: {
            schemas: {
                StartProductTourPayload: {
                    type: "object",
                    properties: {
                        tourId: {
                            $ref: "#/components/schemas/CataloguedTourId",
                            description: "The id of the tour to start. Must be one of the ids in the context."
                        }
                    },
                    required: [
                        "tourId"
                    ]
                },
                CataloguedTourId: {
                    oneOf: [
                        {
                            "const": "homepage-cards-manage-data"
                        },
                        {
                            "const": "component-overview"
                        },
                        {
                            "const": "data-inventory"
                        },
                        {
                            "const": "data-inventory-whats-new-2026-10"
                        },
                        {
                            "const": "discover-experience"
                        },
                        {
                            "const": "answer-surveys"
                        }
                    ]
                }
            }
        },
        schema: {
            $ref: "#/components/schemas/StartProductTourPayload"
        }
    } as import("typia").IJsonSchemaUnit<"3.1">,
    contextDescription: START_PRODUCT_TOUR_CONTEXT_DESCRIPTION,
    isContext: (context): context is StartProductTourContext => (() => { const _io0 = (input: any): boolean => Array.isArray(input.availableTours) && input.availableTours.every((elem: any) => "object" === typeof elem && null !== elem && _io1(elem)) && ("object" === typeof input.tourState && null !== input.tourState && _iu0(input.tourState)); const _io1 = (input: any): boolean => "string" === typeof input.name && "string" === typeof input.description && "string" === typeof input.whenToUse && ("homepage-cards-manage-data" === input.id || "component-overview" === input.id || "data-inventory" === input.id || "data-inventory-whats-new-2026-10" === input.id || "discover-experience" === input.id || "presentation-navigator" === input.id || "answer-surveys" === input.id || "improved-datasets-panel" === input.id); const _io2 = (input: any): boolean => false === input.isActive; const _io3 = (input: any): boolean => true === input.isActive && ("homepage-cards-manage-data" === input.tourId || "component-overview" === input.tourId || "data-inventory" === input.tourId || "data-inventory-whats-new-2026-10" === input.tourId || "discover-experience" === input.tourId || "presentation-navigator" === input.tourId || "answer-surveys" === input.tourId || "improved-datasets-panel" === input.tourId) && "number" === typeof input.stepIndex; const _iu0 = (input: any): any => (() => {
        if (false === input.isActive)
            return _io2(input);
        else if (true === input.isActive)
            return _io3(input);
        else
            return false;
    })(); return (input: any): input is StartProductTourContext => "object" === typeof input && null !== input && _io0(input); })()(context),
    validate: (rawPayload, { availableTours }) => {
        if (!(() => { const _io0 = (input: any): boolean => "homepage-cards-manage-data" === input.tourId || "component-overview" === input.tourId || "data-inventory" === input.tourId || "data-inventory-whats-new-2026-10" === input.tourId || "discover-experience" === input.tourId || "answer-surveys" === input.tourId; return (input: any): input is StartProductTourPayload => "object" === typeof input && null !== input && _io0(input); })()(rawPayload)) {
            return { status: 'rejected', reason: INVALID_PAYLOAD_MESSAGE };
        }
        if (!availableTours.some(({ id }) => id === rawPayload.tourId)) {
            return {
                status: 'rejected',
                reason: 'The requested tour is not available.',
            };
        }
        return { status: 'valid', payload: rawPayload };
    },
    execute: executeStartProductTour,
    isApplied: ({ tourId }, { tourState }) => tourState.isActive && tourState.tourId === tourId,
});
export const buildStartProductTourCommand = (availableTours: TourCatalogueEntry[], tourState: TourState): AiControlledCommand<typeof startProductTourCommandDefinition> => ({
    ...startProductTourCommandDefinition,
    context: { availableTours, tourState },
});
